import { isRecord } from '@fruitful/fruitils';
import {
    AhaMomentImage,
    type AhaMomentIntake,
    type OnboardingChatResults,
} from '@fruitful/types';

import type { ConvMMResults } from './ConversationalMoneyMap';

/**
 * Thread `results` are persisted as the member-app `OnboardingChatResults`
 * shape. Older / partial rows may only have moneyMap + rawChatResults — fill
 * the artifact fields so the Grove carousel can still render.
 */
export const normalizeOnboardingChatResults = (
    results: ConvMMResults | OnboardingChatResults | null | undefined,
): OnboardingChatResults | null => {
    if (results == null || !isRecord(results) || results.moneyMap == null) {
        return null;
    }

    const moneyMap = results.moneyMap as OnboardingChatResults['moneyMap'];
    const artifactData = isRecord(results.artifactData)
        ? (results.artifactData as OnboardingChatResults['artifactData'])
        : {
              debtFreeDate: '',
              slides: '',
              image: AhaMomentImage.Management,
          };

    return {
        moneyMap: {
            incomeTotalCents: Number(moneyMap.incomeTotalCents) || 0,
            billsTotalCents: Number(moneyMap.billsTotalCents) || 0,
            spendTotalCents: Number(moneyMap.spendTotalCents) || 0,
            goalsTotalCents: Number(moneyMap.goalsTotalCents) || 0,
        },
        artifactData: {
            debtFreeDate:
                typeof artifactData.debtFreeDate === 'string'
                    ? artifactData.debtFreeDate
                    : '',
            slides:
                typeof artifactData.slides === 'string'
                    ? artifactData.slides
                    : '',
            image: artifactData.image ?? AhaMomentImage.Management,
            version: artifactData.version,
        },
        rawChatResults: (results.rawChatResults ?? {
            monthlyTakeHomeCents: Number(moneyMap.incomeTotalCents) || 0,
            monthlyExpensesCents: Number(moneyMap.billsTotalCents) || 0,
            monthlySpendCents: Number(moneyMap.spendTotalCents) || 0,
            monthlyGoalsSpendCents: Number(moneyMap.goalsTotalCents) || 0,
        }) as AhaMomentIntake,
        ...(typeof results.highInterestDebtTotalCents === 'number' && {
            highInterestDebtTotalCents: results.highInterestDebtTotalCents,
        }),
        ...(isRecord(results.goalCompletionDates) && {
            goalCompletionDates:
                results.goalCompletionDates as OnboardingChatResults['goalCompletionDates'],
        }),
        ...(isRecord(results.investmentWaterfall) && {
            investmentWaterfall:
                results.investmentWaterfall as OnboardingChatResults['investmentWaterfall'],
        }),
        ...(typeof results.chatVersion === 'string' && {
            chatVersion: results.chatVersion,
        }),
        ...(typeof results.artifactVersion === 'string' && {
            artifactVersion: results.artifactVersion,
        }),
    };
};
